/**
 * whaleSkeleton — a procedural whale-skeleton silhouette generator. Produces
 * normalized geometry (0..1 box) from a seed: a spine curve, ribcage arcs, a
 * blunt skull, and tail flukes. The caller scales to the target width.
 *
 * Deterministic: all randomness via `seededRand` (NO Math.random). Same seed ⇒
 * identical geometry — goldens stable across reloads.
 *
 * Render-agnostic: returns points, not Pixi objects. The `leviathans` kind
 * draws + bakes this; future kinds (parallax creatures, loading screens) can
 * consume the same geometry.
 */
import { seededRand } from '../atmospherePlacement';
import type { Point } from './types';

/** Configurable structural constants (BUILD_STANDARDS §1 — no magic numbers inline). */
const RIB_COUNT_MIN = 10;
const RIB_COUNT_MAX = 16;
const RIB_LENGTH_HEAD = 0.38;   // fraction of bbox height — longest rib (mid-body)
const RIB_LENGTH_TAIL = 0.08;   // shortest rib (tail end)
const SPINE_DROOP = 0.06;       // gentle downward curve head→tail (fraction of height)
const SKULL_LENGTH = 0.12;      // skull as fraction of total length
const SKULL_HEIGHT = 0.22;      // skull height as fraction of bbox height
const FLUKE_LENGTH = 0.08;      // tail flukes as fraction of total length
const FLUKE_SPREAD = 0.12;      // fluke vertical spread (fraction of height)

/**
 * Generate a whale-skeleton silhouette in a normalized 0..1 box.
 * Width = 1.0 (head at x=0, tail at x=1); height derived from the aspect ratio.
 * The caller scales all points by the target pixel width.
 */
export function whaleSkeleton(seed: number, detail = 1): {
  outline: Point[];
  parts: Array<{ points: Point[] }>;
  bbox: { w: number; h: number };
} {
  const ribCount = Math.round(
    RIB_COUNT_MIN + seededRand(seed) * (RIB_COUNT_MAX - RIB_COUNT_MIN),
  ) * Math.max(0.5, Math.min(1.5, detail));

  // Spine: a gentle cubic-ish curve from head to tail with a slight droop.
  const spinePoints: Point[] = [];
  const spineSegments = 24;
  for (let i = 0; i <= spineSegments; i += 1) {
    const t = i / spineSegments;
    const droop = Math.sin(t * Math.PI) * SPINE_DROOP;
    spinePoints.push({ x: t, y: 0.5 + droop });
  }

  // Ribs: perpendicular to the spine, tapering head→tail.
  const parts: Array<{ points: Point[] }> = [];

  // Skull: a blunt hull at the head (x ≈ 0..SKULL_LENGTH).
  const skullCy = 0.5;
  const skullW = SKULL_LENGTH;
  const skullH = SKULL_HEIGHT;
  const skullOutline: Point[] = [
    { x: 0, y: skullCy - skullH * 0.5 },
    { x: skullW * 0.7, y: skullCy - skullH * 0.55 },
    { x: skullW, y: skullCy - skullH * 0.35 },
    { x: skullW, y: skullCy + skullH * 0.35 },
    { x: skullW * 0.7, y: skullCy + skullH * 0.55 },
    { x: 0, y: skullCy + skullH * 0.5 },
  ];
  parts.push({ points: skullOutline });

  // Ribs along the spine (skip the skull zone + the tail fluke zone).
  const ribStart = SKULL_LENGTH;
  const ribEnd = 1.0 - FLUKE_LENGTH;
  const ribStep = (ribEnd - ribStart) / ribCount;
  for (let i = 0; i < ribCount; i += 1) {
    const t = i / Math.max(1, ribCount - 1);
    const x = ribStart + i * ribStep;
    const spineY = 0.5 + Math.sin(x * Math.PI) * SPINE_DROOP;
    // Rib length tapers: longest at mid-body, shortest at the tail.
    const lengthFrac = RIB_LENGTH_HEAD * (1 - t) + RIB_LENGTH_TAIL * t;
    // Slight seeded variation per rib.
    const variation = 0.85 + seededRand(seed + i * 3.3) * 0.3;
    const halfLen = lengthFrac * variation * 0.5;
    // Rib curve: a shallow arc from spine upward + downward.
    const ribPoints: Point[] = [];
    const ribSegments = 4;
    for (let j = 0; j <= ribSegments; j += 1) {
      const r = j / ribSegments;
      const y = spineY - halfLen * Math.sin(r * Math.PI * 0.5);
      ribPoints.push({ x: x + (r - 0.5) * 0.01, y });
    }
    for (let j = ribSegments; j >= 0; j -= 1) {
      const r = j / ribSegments;
      const y = spineY + halfLen * Math.sin(r * Math.PI * 0.5);
      ribPoints.push({ x: x + (r - 0.5) * 0.01, y });
    }
    parts.push({ points: ribPoints });
  }

  // Tail flukes: two diverging strokes at the tail end.
  const flukeX = 1.0 - FLUKE_LENGTH;
  const flukeBaseY = 0.5 + Math.sin(flukeX * Math.PI) * SPINE_DROOP;
  parts.push({
    points: [
      { x: flukeX, y: flukeBaseY },
      { x: 1.0, y: flukeBaseY - FLUKE_SPREAD },
    ],
  });
  parts.push({
    points: [
      { x: flukeX, y: flukeBaseY },
      { x: 1.0, y: flukeBaseY + FLUKE_SPREAD },
    ],
  });

  // Body outline: a convex-ish hull enclosing the skull + ribcage.
  const outline: Point[] = [];
  // Top edge: skull top → arc over the ribcage → taper to tail.
  const outlineSegments = 20;
  for (let i = 0; i <= outlineSegments; i += 1) {
    const t = i / outlineSegments;
    if (t < SKULL_LENGTH) {
      // Skull top.
      const st = t / SKULL_LENGTH;
      outline.push({ x: t, y: skullCy - skullH * 0.5 * (1 - st * 0.3) });
    } else {
      // Ribcage top: highest at ~30% body length, tapering to tail.
      const rt = (t - SKULL_LENGTH) / (1 - SKULL_LENGTH);
      const heightFrac = RIB_LENGTH_HEAD * (1 - rt * 0.9) + RIB_LENGTH_TAIL * rt;
      const spineY = 0.5 + Math.sin(t * Math.PI) * SPINE_DROOP;
      outline.push({ x: t, y: spineY - heightFrac * 0.5 });
    }
  }
  // Bottom edge: tail → arc under the ribcage → skull bottom (reverse).
  for (let i = outlineSegments; i >= 0; i -= 1) {
    const t = i / outlineSegments;
    if (t < SKULL_LENGTH) {
      const st = t / SKULL_LENGTH;
      outline.push({ x: t, y: skullCy + skullH * 0.5 * (1 - st * 0.3) });
    } else {
      const rt = (t - SKULL_LENGTH) / (1 - SKULL_LENGTH);
      const heightFrac = RIB_LENGTH_HEAD * (1 - rt * 0.9) + RIB_LENGTH_TAIL * rt;
      const spineY = 0.5 + Math.sin(t * Math.PI) * SPINE_DROOP;
      outline.push({ x: t, y: spineY + heightFrac * 0.5 });
    }
  }

  return {
    outline,
    parts,
    bbox: { w: 1, h: RIB_LENGTH_HEAD + SPINE_DROOP * 2 + 0.1 },
  };
}
